// Third Party
import get from 'lodash/get';
import moment from 'moment';

// Utils
import { normalizeDataToMonthly } from './calculation-utils';
import { UNIT_BASE, Interval, COMPARISON_ITEM_TYPES, BOND_TAG } from './constants';
import { HOLDING_TYPES } from './portfolios';
import { deepClone } from './objects';
import Dictionary from '@/types/dictionary';
import YearlyIncomeStream from '@/types/yearly-income-stream';
import ComparisonHolding from '@/types/comparison-holding';
import Timestamp from '@/types/timestamp';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import ComparisonItem from '@/types/comparison-item';
import ProposalItem from '@/types/proposal-item';
import IncomeStreamData from '@/types/income-stream-data';
import Comparison from '@/types/comparison';

export enum SCENARIO_FREQUENCY {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum SCENARIO_UNIT {
  PERCENT = '%',
  DOLLAR = '$',
}

export enum SCENARIO_TYPE {
  WITHDRAWAL = 'WITHDRAWAL',
  DEPOSIT = 'DEPOSIT',
}

export const EDIT_SCENARIO_CARD_WIDTH = 45 * UNIT_BASE;
export const DISTRIBUTION_ROWS_PAGE_BREAK = 6;

type NavDataPoint = {
  nav: number;
  weight: number;
  id: string;
  distribution: number;
};

type NavDataPointTuple = {
  timestamp: number;
  data: NavDataPoint[];
};

const getDefaultValues = (): YearlyIncomeStream => ({
  capital: 0,
  distribution: 0,
  months: 0,
  average: 0,
  withdrawals: 0,
  capitalGain: 0,
  distributionPercent: 0,
  gainPercent: 0,
});

const combineDataPoints = (holdings: ComparisonHolding[]): Dictionary<NavDataPoint[]> =>
  holdings.reduce(
    (acc: Dictionary<NavDataPoint[]>, holding) => {
      const normalizedDistributions = holding.distribution ? normalizeDataToMonthly(holding.distribution) : [];

      const distributionMap: Dictionary<number> = normalizedDistributions.reduce((map, obj) => {
        map[obj[0]] = obj[1];
        return map;
      }, {} as Dictionary<number>);

      if (holding.nav) {
        holding.nav[Interval.Monthly].forEach((data: [Timestamp, number]) => {
          const distribution = distributionMap[data[0]] || 0;
          const val: NavDataPoint = { nav: data[1], distribution, weight: holding.weight / 100, id: holding.id };
          const key = data[0];

          if (!acc[key]) {
            acc[key] = [val];
          } else {
            acc[key].push(val);
          }
        });
      }

      return acc;
    },
    {} as Dictionary<NavDataPoint[]>,
  );

const normalizeDataPoints = (navDataPoints: Dictionary<NavDataPoint[]>, holdings: ComparisonHolding[]): void => {
  for (const navDataPoint of Object.values<NavDataPoint[]>(navDataPoints)) {
    if (navDataPoint.length < holdings.length) {
      const existing = navDataPoint.map((i: NavDataPoint) => i.id);
      const missing = holdings.filter(holding => !existing.some((ex: string) => ex === holding.id));
      const weights =
        missing.reduce((acc, curr) => {
          acc += curr.weight;
          return acc;
        }, 0) / 100;

      navDataPoint.forEach(dataPoint => {
        dataPoint.weight /= 1 - weights;
      });

      missing.forEach(m => {
        navDataPoint.push({
          nav: 0,
          weight: 0,
          distribution: 0,
          id: m.id,
        });
      });
    }
    navDataPoint.sort((a: NavDataPoint, b: NavDataPoint) => a.id.localeCompare(b.id));
  }
};

const formatIncomeStreamData = (holdings: ComparisonHolding[]): NavDataPointTuple[] => {
  // combine data into object
  const timeValues = combineDataPoints(holdings);

  // Normalize data for any missing data points plus re-weight for short history
  normalizeDataPoints(timeValues, holdings);

  // Transform object into array
  const sortedTimeValues: NavDataPointTuple[] = Object.keys(timeValues).map((timestamp: string) => ({
    timestamp: Number(timestamp),
    data: timeValues[timestamp],
  }));

  // sort array
  return sortedTimeValues.sort((a, b) => a.timestamp - b.timestamp);
};

const initShareCount = (data: NavDataPoint[], capital: number) => {
  const shareCounts: Map<string, number> = new Map<string, number>();

  for (const navDataPoint of data) {
    const initialNav = navDataPoint.nav;
    const initialDollars = capital * navDataPoint.weight;
    const shareCount: number = initialNav > 0 ? initialDollars / initialNav : 0;
    shareCounts.set(navDataPoint.id, shareCount);
  }

  return shareCounts;
};

/**
 * Apply monthly transactions and returns new share count.
 */
type ReturnTypeForApplyHoldingDistributions = {
  newShareCount: number;
  withdrawals: number;
  distribution: number;
};

const applyHoldingDistributions = (
  distributionHarvest: number,
  distributionPerShare: number,
  shareCount: number,
  pricePerShare: number,
): ReturnTypeForApplyHoldingDistributions => {
  // If there are distributions during the period, buy more shares
  let harvested: number = 0;
  let holdingDistribution: number = 0;
  let newShareCount = shareCount;

  if (distributionPerShare > 0) {
    // Always check if you have enough shares before calculating distribution
    holdingDistribution += shareCount * distributionPerShare;

    // We use 0 or 100 instead of a boolean, to future-proof if we ever need to harvest only parts of the distribution
    if (distributionHarvest === 100) {
      harvested += distributionPerShare * shareCount;
    } else {
      // Allow purchasing of fractional shares
      const sharesToBuy: number = holdingDistribution / pricePerShare;
      newShareCount += sharesToBuy;
    }
  }

  return { newShareCount, withdrawals: harvested, distribution: holdingDistribution };
};

/**
 * Apply monthly transactions and returns new share count.
 */
type ReturnTypeForApplyMonthlyTransaction = {
  newShareCount: number;
  withdrawals: number;
};

const applyMonthlyTransaction = (transaction: number, shareCount: number, pricePerShare: number): ReturnTypeForApplyMonthlyTransaction => {
  // Sell shares based on the withdrawal
  let sharesToSell = pricePerShare > 0 ? transaction / pricePerShare : 0;
  let transactionAmount = 0;
  let newShareCount = shareCount;

  if (sharesToSell > shareCount) {
    sharesToSell = shareCount;
    newShareCount = 0;
  } else {
    newShareCount -= sharesToSell;
  }

  transactionAmount = sharesToSell * pricePerShare;
  return { newShareCount, withdrawals: transactionAmount };
};

const rebalanceWeightsForLongerHistory = (
  navData: NavDataPointTuple[],
  navDataIndex: number,
  startingNavDataIndex: number,
  dataMap: Map<number, number>,
  shareCounts: Map<string, number>,
): Map<string, number> => {
  const { timestamp, data } = navData[navDataIndex];
  const newShareCounts = new Map<string, number>(shareCounts);

  for (let holdingIndex = 0; holdingIndex < data.length; holdingIndex++) {
    const { nav: pricePerShare, weight } = data[holdingIndex];
    const HoldingId = data[holdingIndex].id;
    let shareCount: number = shareCounts.get(HoldingId) || 0;

    // If the weight has changed, we need to adjust the # of shares we own of each type of holding
    const prevWeight: number | undefined =
      navDataIndex > startingNavDataIndex && navData[navDataIndex - 1] ? navData[navDataIndex - 1].data[holdingIndex].weight : undefined;
    const nextWeight: number | undefined = navData[navDataIndex + 1] && navData[navDataIndex + 1].data[holdingIndex].weight;

    /* Handles rebalancing w/ dead holdings (latest NAV data is missing)
    Left of OR: If next weight is 0, we know its a dead holding so we need to sell the shares
    Right of OR: If the next weight is greater than the current weight and the current weight isn't 0, we know
                 we need to rebalance this holding (buy) using the proceeds of the sale of the dead holding
    */
    if (nextWeight !== undefined && (nextWeight === 0 || (nextWeight > weight && weight !== 0))) {
      const currentCapital = dataMap.get(timestamp);
      if (currentCapital) {
        shareCount = nextWeight === 0 ? 0 : (currentCapital * nextWeight) / pricePerShare;
      }
    }

    /* Handles rebalancing w/ short holdings (inception dates that are after the oldest holding in the portfolio)
    Left of OR: If the prev weight is 0 and the current weight isn't 0, we know its a new holding, so we need to purchase shares
    Right of OR: If the prev weight is greater than the current weight, we know we need to rebalance (sell) this holding
                 to account for the short holding
    */
    if (prevWeight !== undefined && ((prevWeight === 0 && weight !== 0) || prevWeight > weight)) {
      if (prevWeight !== 0) {
        const factor = 1 - (prevWeight - weight) / prevWeight;
        shareCount *= factor;
      } else {
        const currentCapital = dataMap.get(timestamp);
        if (currentCapital) {
          shareCount = (currentCapital * weight) / pricePerShare;
        }
      }
    }
    newShareCounts.set(HoldingId, shareCount);
  }

  return newShareCounts;
};

const applyResults = (results: Dictionary<YearlyIncomeStream>): Dictionary<YearlyIncomeStream> => {
  const newResult: Dictionary<YearlyIncomeStream> = deepClone(results);
  for (const year of Object.keys(newResult)) {
    const currentYear = newResult[year];

    if (currentYear.withdrawals > 0) {
      currentYear.average = currentYear.withdrawals / currentYear.months;
    }

    // if deposits (negative withdrawal) or 0 withdrawals then use same value as distributions (100% distributution)
    const gains = currentYear.withdrawals <= 0 ? currentYear.distribution : currentYear.withdrawals;
    const distributionPercent = gains === 0 ? 0 : 100 * (currentYear.distribution / gains);
    currentYear.distributionPercent = Math.min(100, distributionPercent);
    currentYear.gainPercent = gains === 0 ? 0 : 100 - currentYear.distributionPercent;
    currentYear.capitalGain = (currentYear.gainPercent / 100) * gains;
  }

  return newResult;
};

export const getTransactionAmount = (
  monthlyTransaction: number,
  navData: NavDataPointTuple[],
  navDataIndex: number,
  startingNavDataIndex: number,
  holdingIndex: number,
) => {
  /**
   * If the weight has changed, the transaction amount needs to reflect the weight before re-balancing occurs
   * so that the capital is maximized from any distributions paid so if re-balancing is
   * happening then we take the old weight before the weights shift to the newer holdings
   * This ensures that if the client is re-balancing due to short history , the withdrawal amount stays the same regardless of change in weights
   */
  const { data } = navData[navDataIndex];
  const { weight } = data[holdingIndex];

  const prevWeight: number | null =
    navDataIndex > startingNavDataIndex && navData[navDataIndex - 1] != null ? navData[navDataIndex - 1].data[holdingIndex].weight : null;
  const effectiveWeight = prevWeight != null && prevWeight !== weight ? prevWeight : weight;
  return monthlyTransaction * effectiveWeight;
};

export const calculateIncomeStreamData = (holdings: ComparisonHolding[], scenario: IncomeStreamScenario, startDate: Timestamp, endDate: Timestamp) => {
  let results: Dictionary<YearlyIncomeStream> = {};
  const dataMap = new Map<number, number>();

  // Calculate monthly monthly Transaction, (withdrawal or deposit) invert if it's a deposit
  let monthlyTransaction = scenario.amount / (scenario.frequency === SCENARIO_FREQUENCY.YEARLY ? 12 : 1);
  monthlyTransaction *= scenario.type === SCENARIO_TYPE.DEPOSIT ? -1 : 1;

  const navData: NavDataPointTuple[] = formatIncomeStreamData(holdings).filter(d => d.timestamp <= endDate);

  const startingNavDataIndex = navData.findIndex(n => n.timestamp >= startDate);
  if (startingNavDataIndex === -1) {
    return null;
  }

  // shareCounts holds the current share count per holding index
  let shareCounts = initShareCount(navData[startingNavDataIndex].data, scenario.capital);

  // loop all monthly data points
  for (let navDataIndex = startingNavDataIndex; navDataIndex < navData.length; navDataIndex++) {
    // Initial shares (fractional) - That's the base of the calcs
    const { timestamp, data } = navData[navDataIndex];

    // loop all holdings
    for (let holdingIndex = 0; holdingIndex < data.length; holdingIndex++) {
      const holdingId = data[holdingIndex].id;
      const holding = holdings.find(h => h.id === holdingId);

      if (holding != null && holding.nav != null) {
        let shareCount: number = shareCounts.get(holdingId) || 0;
        // transaction can be a WITHDRAWAL or DEPOSIT

        const { distribution: distributionPerShare, nav: pricePerShare } = data[holdingIndex];

        let transactionAmount: number = getTransactionAmount(monthlyTransaction, navData, navDataIndex, startingNavDataIndex, holdingIndex);

        // Format the date
        const dateObject: Date = new Date(timestamp);
        const normalizedDate = new Date(Date.UTC(dateObject.getUTCFullYear(), dateObject.getUTCMonth() + 1, 0)).getTime();
        const currentYear: string = dateObject.getUTCFullYear().toString();
        results[currentYear] = results[currentYear] || getDefaultValues();

        if (distributionPerShare > 0) {
          const res: ReturnTypeForApplyHoldingDistributions = applyHoldingDistributions(
            scenario.distributionHarvest,
            distributionPerShare,
            shareCount,
            pricePerShare,
          );
          shareCount = res.newShareCount;
          results[currentYear].distribution += res.distribution;
          results[currentYear].withdrawals += res.withdrawals;
        }

        let currentValue = shareCount * pricePerShare;
        if (scenario.unit === SCENARIO_UNIT.PERCENT) {
          transactionAmount = currentValue * (monthlyTransaction / 100);
        }

        if (navDataIndex > startingNavDataIndex) {
          const res: ReturnTypeForApplyMonthlyTransaction = applyMonthlyTransaction(transactionAmount, shareCount, pricePerShare);
          shareCount = res.newShareCount;
          results[currentYear].withdrawals += res.withdrawals;

          // only increment the number of months for the first holding
          if (holdingIndex === 0 && results[currentYear].months < 12) {
            results[currentYear].months++;
          }
        }

        currentValue = shareCount * pricePerShare;

        // Set the current value as the value for that year if it's the last month or the last point in the set
        if (dateObject.getUTCMonth() === 11 || navDataIndex === navData.length - 1) {
          results[currentYear].capital += currentValue;
        }

        // Add the value to the specific date, summing existing data-points to merge holdings together
        dataMap.set(normalizedDate, (dataMap.get(normalizedDate) || 0) + currentValue);
        shareCounts.set(holdingId, shareCount);
      } else {
        // Fund is missing nav data
        return null;
      }
    }

    // loop again to rebalance
    shareCounts = rebalanceWeightsForLongerHistory(navData, navDataIndex, startingNavDataIndex, dataMap, shareCounts);
  }

  results = applyResults(results);

  return {
    results,
    graphData: Array.from(dataMap).sort((a, b) => a[0] - b[0]),
  } as IncomeStreamData;
};

/**
 * Returns array of years based on calculated income stream data.
 *
 * @param {ComparisonItem[] | ProposalItem[]} items Proposal or Comparison Items.
 * @return {string[]} Array of years
 */
export const getDataYears = (items?: ComparisonItem[] | ProposalItem[]): string[] => {
  const yearSet = new Set<string>();
  for (const item of items || []) {
    const results = get(item, 'scenarios.defaultIncomeStream.data.results', {});
    Object.keys(results).forEach(y => yearSet.add(y));
  }

  const years = Array.from(yearSet).sort();

  return years.reverse();
};

/**
 * Checks if item has stock or bonds
 * @param {ComparisonItem} item the item to validate
 */
export const doesItemHaveStocksOrBonds = (item: ComparisonItem) =>
  // TODO - When we fix stock data we can remove this.
  item.holdings.some(h => h.type === HOLDING_TYPES.STOCK || h.id?.startsWith(BOND_TAG));

/**
 * Checks if item is valid for income stream
 * @param {ComparisonItem} item the item to validate
 */
export const validIncomeStreamItem = (item: ComparisonItem) => item.type !== COMPARISON_ITEM_TYPES.BENCHMARK && !doesItemHaveStocksOrBonds(item);

/**
 * Filters valid comparison items for income stream
 * @param {ComparisonItem[]} items the items to filter
 */
export const filterIncomeStreamItems = (items: ComparisonItem[]) => items.filter(item => validIncomeStreamItem(item));

/**
 * Gets the income stream start date. Use date from options or default to 5Y.
 *
 * @param {Comparison} comparison Comparison
 * @return {Date} income stream start date
 */
export const getIncomeStartDate = (comparison: Comparison) => {
  const DEFAULT_INCOME_STREAM_DATE = moment().startOf('year').subtract(5, 'years').toDate();

  return (comparison.options && comparison.options.income && comparison.options.income.startDate) || DEFAULT_INCOME_STREAM_DATE;
};

export const getIncomeEndDate = (comparison: Comparison) =>
  (comparison.options && comparison.options.income && comparison.options.income.endDate) || new Date();

/**
 * Checks if a comparison item has an income stream scenario.
 *
 * @param {items} ComparisonItem[] items
 * @return {boolean} `True` if comparison has an item with income stream scenarios. `False` otherwise.
 */
export const hasIncomeStreamScenarios = (items: ComparisonItem[]) => items.some(i => !!i.scenarios);

/**
 * Returns the total number of distribution scenarios for all items
 *
 * @param {items} ComparisonItem[] items
 * @return {number} Number of distribution scenarios
 */
export const getNumDistributionScenarios = (items: ComparisonItem[]) =>
  items.reduce((acc, cur) => {
    if (!cur.scenarios || !cur.scenarios.incomeStreamDistribution) {
      return acc;
    }

    return acc + cur.scenarios.incomeStreamDistribution.length;
  }, 0);

/**
 * Calculates the monthly NAVs from rolling performance
 * @param {ComparisonHolding} holding the holding to calculate NAV for
 */
export const calculateMonthlyNAVsFromRolling = (holding: ComparisonHolding): [Timestamp, number][] => {
  if (!holding.rolling || !holding.rolling.monthly.length) {
    return [];
  }

  const navs: [Timestamp, number][] = [];

  for (let i = 0; i < holding.rolling.monthly.length; i++) {
    const currentTimestamp = holding.rolling.monthly[i][0];
    const multiplier = i === 0 ? 1 : navs[i - 1][1];
    const value = multiplier * (1 + holding.rolling.monthly[i][1] / 100);

    navs.push([currentTimestamp, value]);
  }
  return navs;
};

// --- Unit Tests Access ---
// this is a temporary solution. Eric is looking into including a librry (maybe rewire) so we can access
// the non-exported stuff from the tests themself. For nor, the tested code must provide access.

export const UnitTestAccess = {
  normalizeDataPoints,
  combineDataPoints,
  rebalanceWeightsForLongerHistory,
  initShareCount,
  applyHoldingDistributions,
  applyMonthlyTransaction,
  getTransactionAmount,
};

export type UnitTestNavDataPoint = NavDataPoint;
export type UnitTestNavDataPointTuple = NavDataPointTuple;
export type UnitTestReturnTypeForApplyHoldingDistributions = ReturnTypeForApplyHoldingDistributions;
export type UnitTestReturnTypeForApplyMonthlyTransaction = ReturnTypeForApplyMonthlyTransaction;
